import { styles } from "@repo/helpers";
import { Card, CardContent, CardHeader } from "@repo/ui";
import { Check, CircleCheck, CircleFadingPlus, CircleX, Plus, X } from "lucide-react";
import { FC } from "react";
import { Link } from "react-router-dom";

import { useRepContext } from "../../contexts/editableItemContext";
import OnboardingCompletion, { OnboardingRequirements } from "../../models/onboardingRequirement";
import style from "./style.module.scss";

export interface Req {
	id: string & keyof OnboardingRequirements;
	href: string;
}

interface ReqProps {
	req: Req;
	completion: OnboardingCompletion | undefined;
}

interface SubRequirementsProps {
	completion: OnboardingCompletion;
}

const SubRequirements: FC<SubRequirementsProps> = ({ completion }) => {
	let maxCompletedSubReqs = -1;
	let maxCompletedKey = null;

	// If there are multiple bgc/dt, it will render multiple sub requirements
	// This looks pretty bad and can be hard to read.
	// This will filter out the completion that is the most complete
	// so that when you look at the req you see the minimum number of updates
	Object.keys(completion.iterated).forEach((keyString) => {
		const key = parseInt(keyString);

		if (completion.iterated[key].done !== completion.done) return;

		const values: OnboardingCompletion[] = completion.iterated[key].subRequirements.filter(
			(subReq) => subReq.required || subReq.contingentRequired,
		);

		const completedCount = values.filter((subReq) => subReq.done).length;

		if (completedCount <= maxCompletedSubReqs) {
			return;
		}
		maxCompletedSubReqs = completedCount;
		maxCompletedKey = key;
	});

	if (maxCompletedKey !== null) {
		const values: OnboardingCompletion[] = completion.iterated[maxCompletedKey].subRequirements.filter(
			(subReq) => subReq.required || subReq.contingentRequired,
		);

		return (
			<>
				{values.map((value) => (
					<div key={value.name} className={style.flexRow}>
						<ReqIcon completion={value} small />
						<span className={styles(style.textSmMuted, "text-start")}>{value.name}</span>
					</div>
				))}
			</>
		);
	}

	return null;
};

export const Req: FC<ReqProps> = ({ req, completion }) => {
	const { repId } = useRepContext();
	if (!completion) return null;
	const requiredText = completion.required ? "Required" : "Not Required";

	return (
		<Link to={`/AgentPortal/${repId}/${req.href}`} key={req.id} className={style.link}>
			<Card className={style.card}>
				<span className={style.textXsMuted}>{requiredText}</span>
				<CardHeader className={style.cardHeader}>
					<ReqIcon completion={completion} />
					<h3 className={style.cardTitle}>{completion.name}</h3>
				</CardHeader>
				<CardContent className="mx-auto px-0">
					<div className="text-center">
						<SubRequirements completion={completion} />
						{!completion.done && <span className={style.textSmMuted}>{completion.reasonNotDone}</span>}
					</div>
				</CardContent>
			</Card>
		</Link>
	);
};

export function ReqIcon({ completion, small }: { completion: OnboardingCompletion; small?: boolean }) {
	let color = style.textRed;
	if (completion.done) color = style.textGreen;
	else if (completion.contingentDone) color = style.textOrange;
	else if (completion.inProgress) color = style.textBlue;
	const className = styles(color, style.reqIcon, small && style.small);
	if (completion.done || completion.contingentDone) {
		if (small) return <Check className={className} />;
		return <CircleCheck className={className} />;
	}
	if (completion.inProgress) {
		if (small) return <Plus className={className} />;
		return <CircleFadingPlus className={className} />;
	}
	if (small) return <X className={className} />;
	return <CircleX className={className} />;
}
